import { useState } from "react";
import { UserX } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Modal } from "./shared";

export function PurgeRetiredPanel() {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);

  const purge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/tournaments/purge-retired");
      const n = r.data.removed_count || 0;
      setResult(r.data);
      setConfirm(false);
      toast.success(n === 0 ? "No retired fighters were stuck in sign-ups." : `Removed ${n} retired entrant${n === 1 ? "" : "s"} and refunded each entry.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="purge-retired-panel" className="mb-6 border border-amber-700/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <UserX size={16} className="text-amber-400" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Purge Retired from Tournaments</h3>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-zinc-500 flex-1 min-w-[200px]">
          Retroactively removes <span className="text-amber-300">retired or deleted gladiators</span> still stuck in open tournament sign-ups, and <span className="text-amber-300">refunds each entry fee</span>. Locked/running brackets are untouched.
        </p>
        <button data-testid="purge-retired-btn" onClick={() => setConfirm(true)} disabled={busy}
          className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50">
          {busy ? "Purging…" : "Run Purge"}
        </button>
      </div>

      {result && (
        <div data-testid="purge-retired-result" className="mt-4 pt-3 border-t border-amber-900/30 text-sm">
          <p className="text-zinc-300 mb-2">Removed <span className="text-amber-300 font-semibold">{result.removed_count}</span> entrant{result.removed_count === 1 ? "" : "s"}.</p>
          {(result.removed || []).length > 0 && (
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {result.removed.map((it, i) => (
                <li key={i} className="text-xs text-zinc-500 tabular-nums">
                  {it.name || it.gladiator_name || it.gladiator_id || "Unknown"}
                  {it.bracket ? ` · ${it.bracket}` : ""}{it.refund ? ` · refunded ${it.refund}g` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confirm && (
        <Modal title="Confirm Purge" testid="purge-retired-confirm" onClose={() => setConfirm(false)}>
          <p className="text-sm text-zinc-300">Remove all retired/deleted fighters from open tournament sign-ups and refund their entries? This only affects tournaments still in sign-ups.</p>
          <div className="mt-5 flex gap-2 justify-end">
            <button data-testid="purge-retired-cancel" onClick={() => setConfirm(false)} disabled={busy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
            <button data-testid="purge-retired-confirm-btn" onClick={purge} disabled={busy} className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm disabled:opacity-50">{busy ? "Purging…" : "Confirm"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
